import { cn, Typography } from "@norican/react";

import { useT } from "@/i18n";
import { useMe } from "@/lib/api";
import { useKioskPerson } from "@/lib/kiosk-person";

import { useKioskNotYou } from "./use-kiosk-not-you";
import { useSignOut } from "./use-sign-out";

export interface FilingAsLineProps {
  className?: string;
}

/**
 * "Filing as Astrid Lindqvist · Not you? Sign out", beside a Submit button.
 *
 * Shop-floor PCs are shared, and a ticket filed under the previous person's
 * session goes to the wrong inbox. Saying whose name it goes out under, right
 * where people press Submit, is the cheapest catch. Renders nothing until the
 * profile has loaded rather than flashing a blank name.
 *
 * On a shared device the session is the device's, so the line names the
 * person who typed their name in, and "Not you?" signs the device out, so the
 * next person starts a fresh, private sign-in (2 Oct).
 */
export function FilingAsLine({ className }: FilingAsLineProps) {
  const { t } = useT();
  const { data: me } = useMe();
  const { signOut, isPending } = useSignOut();
  const kioskPerson = useKioskPerson();
  const kioskNotYou = useKioskNotYou();

  if (me === undefined) {
    return null;
  }
  const onKiosk = me.kiosk != null;
  const name = onKiosk
    ? (kioskPerson?.name ?? "")
    : [me.first_name, me.last_name].filter(Boolean).join(" ") || me.email;

  return (
    <Typography
      variant="caption"
      tone="muted"
      className={cn("flex min-w-0 flex-wrap items-baseline gap-x-1", className)}
    >
      <span className="min-w-0 truncate">{t("filingAs.line", { name })}</span>
      <span aria-hidden="true">·</span>
      <span>
        {t("filingAs.notYou")}{" "}
        {onKiosk ? (
          <button
            type="button"
            onClick={kioskNotYou}
            className="text-accent-fg-subtle font-medium underline-offset-2 hover:underline"
          >
            {t("user.signOut")}
          </button>
        ) : (
          <button
            type="button"
            onClick={() => void signOut()}
            disabled={isPending}
            className="text-accent-fg-subtle font-medium underline-offset-2 hover:underline disabled:opacity-60"
          >
            {isPending ? t("user.signingOut") : t("user.signOut")}
          </button>
        )}
      </span>
    </Typography>
  );
}
